import React, { useState, useEffect } from 'react';
import UIModal from '@/components/shared/UIModal';
import UIButton from '@/components/shared/UIButton';
import UIBadge from '@/components/shared/UIBadge';
import { useTickets } from '@/hooks/useTickets';
import { TicketDetails } from '@/types/tickets';
import './TicketDetailsModal.scss';
import { safeToLowerCase } from '@/utils/stringUtils';

interface TicketDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticketId: string | null;
}

const formatDate = (dateString: string | null) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

const getStatusBadgeVariant = (status: string) => {
  switch (safeToLowerCase(status)) {
    case 'open':
      return 'primary';
    case 'in_progress':
      return 'info';
    case 'resolved':
      return 'success';
    case 'closed':
      return 'success';
    case 'on_hold':
      return 'warning';
    default:
      return 'secondary';
  }
};

const TicketDetailsModal: React.FC<TicketDetailsModalProps> = ({
  isOpen,
  onClose,
  ticketId,
}) => {
  const [ticket, setTicket] = useState<TicketDetails | null>(null);
  const { getTicketDetails, loading, error } = useTickets();

  useEffect(() => {
    if (isOpen && ticketId) {
      const fetchTicketDetails = async () => {
        const details = await getTicketDetails(ticketId);
        if (details) {
          setTicket(details);
        }
      };
      fetchTicketDetails();
    } else if (!isOpen) {
      setTicket(null);
    }
  }, [isOpen, ticketId, getTicketDetails]);

  const tenant = ticket?.lease?.tenant;
  const property = ticket?.lease?.unit?.property;

  return (
    <UIModal
      isOpen={isOpen}
      onClose={onClose}
      title="Ticket Details"
      size="large"
    >
      <div className="TicketDetailsModal">
        {loading && (
          <div className="TicketDetailsModal__loading">
            <div className="TicketDetailsModal__spinner"></div>
            <span>Loading ticket details...</span>
          </div>
        )}

        {error && (
          <div className="TicketDetailsModal__error">
            <span>⚠️ Error loading ticket details</span>
            <p>{error}</p>
          </div>
        )}

        {ticket && !loading && (
          <>
            <div className="TicketDetailsModal__layout">
              {/* Main Content */}
              <div className="TicketDetailsModal__main">
                <div className="TicketDetailsModal__header">
                  <h2 className="TicketDetailsModal__subject">
                    {ticket.title}
                  </h2>
                  <UIBadge
                    variant={getStatusBadgeVariant(ticket.status)}
                    shape="default"
                  >
                    {ticket.status?.toUpperCase()}
                  </UIBadge>
                </div>

                {ticket.description && (
                  <div className="TicketDetailsModal__section">
                    <h3 className="TicketDetailsModal__section-title">
                      Description
                    </h3>
                    <p className="TicketDetailsModal__description-text">
                      {ticket.description}
                    </p>
                  </div>
                )}

                {ticket.resolution && (
                  <div className="TicketDetailsModal__section">
                    <h3 className="TicketDetailsModal__section-title">
                      Resolution
                    </h3>
                    <p className="TicketDetailsModal__description-text">
                      {ticket.resolution}
                    </p>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="TicketDetailsModal__sidebar">
                <div className="TicketDetailsModal__section">
                  <h3 className="TicketDetailsModal__section-title">
                    Ticket Information
                  </h3>
                  <div className="TicketDetailsModal__field">
                    <span className="TicketDetailsModal__label">Ticket ID</span>
                    <span className="TicketDetailsModal__value TicketDetailsModal__value--mono">
                      {ticket.id}
                    </span>
                  </div>
                  <div className="TicketDetailsModal__field">
                    <span className="TicketDetailsModal__label">
                      Created At
                    </span>
                    <span className="TicketDetailsModal__value">
                      {formatDate(ticket.createdAt)}
                    </span>
                  </div>
                  <div className="TicketDetailsModal__field">
                    <span className="TicketDetailsModal__label">
                      Last Updated
                    </span>
                    <span className="TicketDetailsModal__value">
                      {formatDate(ticket.updatedAt)}
                    </span>
                  </div>
                </div>

                {tenant && (
                  <div className="TicketDetailsModal__section">
                    <h3 className="TicketDetailsModal__section-title">
                      👤 Tenant Information
                    </h3>
                    <div className="TicketDetailsModal__field">
                      <span className="TicketDetailsModal__label">Name</span>
                      <span className="TicketDetailsModal__value">{`${tenant.firstname} ${tenant.lastname}`}</span>
                    </div>
                    <div className="TicketDetailsModal__field">
                      <span className="TicketDetailsModal__label">Email</span>
                      <span className="TicketDetailsModal__value TicketDetailsModal__value--link">
                        {tenant.email}
                      </span>
                    </div>
                    {tenant.phone && (
                      <div className="TicketDetailsModal__field">
                        <span className="TicketDetailsModal__label">Phone</span>
                        <span className="TicketDetailsModal__value">
                          {tenant.phone}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {property && (
                  <div className="TicketDetailsModal__section">
                    <h3 className="TicketDetailsModal__section-title">
                      🏢 Property Details
                    </h3>
                    <div className="TicketDetailsModal__field">
                      <span className="TicketDetailsModal__label">
                        Property
                      </span>
                      <span className="TicketDetailsModal__value">
                        {property.name}
                      </span>
                    </div>
                    <div className="TicketDetailsModal__field">
                      <span className="TicketDetailsModal__label">Type</span>
                      <span className="TicketDetailsModal__value TicketDetailsModal__value--capitalize">
                        {property.type || 'No units'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="TicketDetailsModal__actions">
              <UIButton
                variant="secondary"
                size="medium"
                outline={true}
                onClick={onClose}
              >
                Close
              </UIButton>
              {ticket.status !== 'CLOSED' && ticket.status !== 'RESOLVED' && (
                <UIButton
                  variant="primary"
                  size="medium"
                  onClick={() => console.log('Mark as resolved', ticket.id)}
                >
                  Mark as Resolved
                </UIButton>
              )}
            </div>
          </>
        )}
      </div>
    </UIModal>
  );
};

export default TicketDetailsModal;
